import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useLocation } from 'react-router-dom';
import Analytics from '@/lib/mixpanel';
import { signInWithEmailWrapper, signUpWithEmailWrapper, signInWithGoogleWrapper } from '@/lib/auth-wrapper';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<{ error: any }>;
  signUpWithEmail: (email: string, password: string, metadata?: { full_name: string; date_of_birth: string }) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  forceSignOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Use ref to track previous user state to avoid stale closure issues
  const previousUserRef = useRef<User | null>(null);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const wasAuthenticated = !!previousUserRef.current;

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Handle redirects based on auth state - but only for genuine auth changes
        if (event === 'SIGNED_IN' && session) {
          // Defer async operations to prevent deadlock
          setTimeout(() => {
            // Check if this is a new user (created within last 60 seconds) for Google Ads conversion
            const createdAt = new Date(session.user.created_at || 0);
            const now = new Date();
            const isNewUser = (now.getTime() - createdAt.getTime()) < 60000; // 60 seconds

            console.log('[AUTH] SIGNED_IN event:', { wasAuthenticated, isNewUser, email: session.user.email });

            // Only process new sign-ins, not token refreshes
            if (!wasAuthenticated) {
              if (isNewUser) {
                // Track Google signup in Mixpanel
                Analytics.trackSignUp('google', {
                  source: localStorage.getItem('pendingAction') === 'create_journal' ? 'auth_modal' : 'direct'
                });
                console.log('📊 Mixpanel: Google sign-up tracked');

                // Track Google Ads conversion
                if (window.gtag_report_conversion) {
                  window.gtag_report_conversion();
                  console.log('📊 Google Ads conversion tracked for new sign-up');
                }
                
                // Send welcome email for new Google users (fire-and-forget)
                console.log('[AUTH] Sending welcome email for new user:', session.user.email);
                supabase.functions.invoke('send-welcome-email', {
                  body: {
                    email: session.user.email,
                    name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || ''
                  }
                }).then(result => {
                  console.log('[AUTH] Welcome email result:', result);
                }).catch(err => console.log('Welcome email failed (non-critical):', err));
              }

              // Don't redirect if user is on welcome video page - let them complete it first
              if (location.pathname === '/welcome-video') {
                console.log('🎥 User is on welcome video page, skipping auto-redirect');
                return;
              }

              // Let welcome video flow handle navigation for new signups
              console.log('🏠 User signed in, staying on current page or going to home');
              sessionStorage.removeItem('fresh_signup');
              if (location.pathname === '/login' || location.pathname === '/signup') {
                navigate('/');
              }
            } else {
              // User was already authenticated - this is likely a token refresh
              console.log('🔄 Session refresh detected, staying on current page');
            }
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          // Defer navigation to prevent issues with auth state changes
          setTimeout(() => {
            // Redirect to landing page if on protected route
            if (location.pathname !== '/login' && location.pathname !== '/signup') {
              console.log('🔐 Sign out detected, redirecting to landing');
              navigate('/');
            }
          }, 0);
        }

        // Update the previous user reference
        previousUserRef.current = session?.user ?? null;
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.log('Error getting session:', error);
        // Clear any potentially invalid session
        setSession(null);
        setUser(null);
        previousUserRef.current = null;
      } else {
        setSession(session);
        setUser(session?.user ?? null);
        previousUserRef.current = session?.user ?? null;
      }
      setLoading(false);
      setIsInitialized(true);
    });

    return () => subscription.unsubscribe();
  }, [navigate, location.pathname]);

  const signInWithEmail = async (email: string, password: string) => {
    const { error } = await signInWithEmailWrapper(email, password);

    if (!error) {
      Analytics.trackSignIn('email');
    }

    return { error };
  };

  const signUpWithEmail = async (email: string, password: string, metadata?: { full_name: string; date_of_birth: string }) => {
    // Mark as fresh signup to help coordinate with other redirect logic
    sessionStorage.setItem('fresh_signup', 'true');

    const { error } = await signUpWithEmailWrapper(email, password, metadata);

    if (!error) {
      Analytics.trackSignUp('email', {
        has_full_name: !!metadata?.full_name,
        has_date_of_birth: !!metadata?.date_of_birth
      });

      // Set up Stripe customer and send welcome email in background
      setTimeout(async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            // Setup Stripe customer
            await supabase.functions.invoke('setup-new-user');
            
            // Send welcome email (fire-and-forget)
            supabase.functions.invoke('send-welcome-email', {
              body: {
                email: session.user.email,
                name: metadata?.full_name || session.user.user_metadata?.full_name || ''
              }
            }).catch(err => console.log('Welcome email failed (non-critical):', err));
          }
        } catch (error) {
          console.log('Setup new user failed (non-critical):', error);
        }
      }, 1000);
    } else {
      // Clear flag if signup failed
      sessionStorage.removeItem('fresh_signup');
    }

    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await signInWithGoogleWrapper();

    if (!error) {
      Analytics.trackSignIn('google');
    }

    return { error };
  };

  // Comprehensive cleanup of user-specific data
  const cleanupUserData = () => {
    try {
      // Clean up localStorage items
      const keysToRemove = [
        'agentDockPosition',
        'studyReminderSettings',
        'use_gemini_live_protocol'
      ];

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      // Clean up all study note backups (keys starting with 'study-note-')
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('study-note-')) {
          localStorage.removeItem(key);
        }
      }

      // Clean up sessionStorage items
      sessionStorage.removeItem('fresh_signup');

    } catch (error) {
      console.log('Cleanup error (non-critical):', error);
    }
  };

  const signOut = async () => {
    console.log('🚪 Starting sign out process...');

    try {
      Analytics.trackSignOut();

      // FORCE clear ALL auth-related storage immediately
      localStorage.clear();
      sessionStorage.clear();

      // Force clear local session state
      setSession(null);
      setUser(null);
      previousUserRef.current = null;
      setLoading(false);

      console.log('🧹 Local state cleared, attempting Supabase signout...');

      // Use signOut with scope: 'local' to avoid server-side issues
      await supabase.auth.signOut({ scope: 'local' });

      console.log('✅ Supabase local signout complete');

      Analytics.reset();

      // Force navigate to landing with replace to clear history
      navigate('/', { replace: true });

      // Force page reload to ensure clean state
      setTimeout(() => {
        window.location.href = '/';
      }, 100);

    } catch (error) {
      console.log('⚠️ Sign out error, forcing cleanup:', error);

      // Nuclear option: clear everything and force reload
      localStorage.clear();
      sessionStorage.clear();
      setSession(null);
      setUser(null);
      previousUserRef.current = null;
      Analytics.reset();

      // Force page reload to landing
      window.location.href = '/';
    }
  };

  const forceSignOut = () => {
    // Immediately clear all local state
    setSession(null);
    setUser(null);
    previousUserRef.current = null;

    // Clean up all user-specific data
    cleanupUserData();

    Analytics.reset();

    // Navigate to landing
    navigate('/');
  };

  const value = {
    user,
    session,
    loading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    forceSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};